import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import SingleFilter from '../../../../../components/SelectFilters/SingleFilter';
import { FlairMultiSelectDropdown } from '../../../../../atomic/molecules/FlairDropdown/FlairMultiSelectDropdown/FlairMultiSelectDropdown';
import type { FilterSelectOption } from '../../../../../atomic/molecules/FlairDropdown/types';
import { VIEW_DAY, VIEW_WEEK, type ViewType } from './types';
import type {
  SchedulerOption,
  TargetOption,
} from './useColleaguesCalendarData';
import { ToolbarLoading } from './loading/ToolbarLoading';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';

type Props = {
  view: ViewType;
  onViewChange: (view: ViewType) => void;
  schedulers: SchedulerOption[];
  selectedScheduler: SchedulerOption | null;
  onSchedulerChange: (scheduler: SchedulerOption | null) => void;
  targets: TargetOption[];
  selectedTargetIds: string[];
  onTargetsChange: (targetIds: string[]) => void;
  onNavigatePrevious: () => void;
  onNavigateNext: () => void;
  onNavigateToday: () => void;
  loading?: boolean;
  showOpenShifts: boolean;
  onShowOpenShiftsChange: (value: boolean) => void;
  openShiftsCount: number;
};

const isViewType = (value: string | null): value is ViewType =>
  value === VIEW_DAY || value === VIEW_WEEK;

const ColleaguesToolbar: React.FC<React.PropsWithChildren<Props>> = ({
  view,
  onViewChange,
  schedulers,
  selectedScheduler,
  onSchedulerChange,
  targets,
  selectedTargetIds,
  onTargetsChange,
  onNavigatePrevious,
  onNavigateNext,
  onNavigateToday,
  loading,
  showOpenShifts,
  onShowOpenShiftsChange,
  openShiftsCount,
}) => {
  const t = useNamespacedTranslation('schedulerShifts.colleaguesCalendar');

  const viewOptions: FilterSelectOption[] = useMemo(
    () => [
      { value: VIEW_DAY, label: t('day') },
      { value: VIEW_WEEK, label: t('week') },
    ],
    [t],
  );
  const schedulerOptions: FilterSelectOption[] = useMemo(
    () => schedulers.map((s) => ({ value: s.Id, label: s.Name })),
    [schedulers],
  );

  const targetOptions: FilterSelectOption[] = useMemo(
    () => targets.map((target) => ({ value: target.id, label: target.name })),
    [targets],
  );

  const handleSchedulerChange = useCallback(
    (value: string | null) => {
      const scheduler = schedulers.find((s) => s.Id === value) ?? null;
      onSchedulerChange(scheduler);
    },
    [schedulers, onSchedulerChange],
  );

  const handleViewChange = useCallback(
    (value: string | null) => {
      if (isViewType(value)) {
        onViewChange(value);
      }
    },
    [onViewChange],
  );

  const handleTargetsChange = useCallback(
    (selected: readonly FilterSelectOption[]) => {
      onTargetsChange(selected.map((o) => o.value));
    },
    [onTargetsChange],
  );

  return (
    <div className="d-flex align-items-center gap-3 py-2 flex-wrap">
      {loading ? (
        <ToolbarLoading />
      ) : (
        <>
          {schedulers.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              <span className="text-nowrap small text-muted">
                {t('scheduler')}
              </span>
              <div style={{ minWidth: '14rem' }}>
                <SingleFilter
                  options={schedulerOptions}
                  value={selectedScheduler?.Id ?? null}
                  onChange={handleSchedulerChange}
                  isClearable={false}
                  isSearchable={false}
                />
              </div>
            </div>
          )}

          {selectedScheduler && (
            <div style={{ minWidth: '14rem' }}>
              <FlairMultiSelectDropdown
                options={targetOptions}
                selectedOptions={selectedTargetIds}
                onChange={handleTargetsChange}
                placeholder={
                  targetOptions.length === 0
                    ? t('noRecordsFound')
                    : t('targets')
                }
                isDisabled={targetOptions.length === 0}
              />
            </div>
          )}

          {openShiftsCount > 0 && (
            <Form.Check
              type="checkbox"
              id="show-open-shifts-colleagues"
              label={t('showOpenShifts', { count: openShiftsCount })}
              checked={showOpenShifts}
              onChange={(e) => onShowOpenShiftsChange(e.target.checked)}
              className="mb-0"
            />
          )}
        </>
      )}

      <div className="ms-auto d-flex align-items-center gap-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-nowrap small text-muted">{t('viewBy')}</span>
          <div style={{ minWidth: '6rem' }}>
            <SingleFilter
              options={viewOptions}
              value={view}
              onChange={handleViewChange}
              isClearable={false}
              isSearchable={false}
            />
          </div>
        </div>

        <ButtonGroup>
          <Button
            size="sm"
            variant="light"
            onClick={onNavigatePrevious}
            aria-label={t('previous')}
            title={t('previous')}>
            {'←'}
          </Button>
          <Button size="sm" variant="light" onClick={onNavigateToday}>
            {t('today')}
          </Button>
          <Button
            size="sm"
            variant="light"
            onClick={onNavigateNext}
            aria-label={t('next')}
            title={t('next')}>
            {'→'}
          </Button>
        </ButtonGroup>
      </div>
    </div>
  );
};

export default ColleaguesToolbar;
